import { useEffect, useRef, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface UseSessionOptions {
  timeoutMinutes?: number
  warningMinutes?: number
  onTimeout?: () => void
  onWarning?: () => void
}

export const useSession = (options: UseSessionOptions = {}) => {
  const {
    timeoutMinutes = 15,
    warningMinutes = 2,
    onTimeout,
    onWarning
  } = options

  const { logout, isAuthenticated } = useAuth()
  const timeoutRef = useRef<number | null>(null)
  const warningRef = useRef<number | null>(null)
  const lastActivityRef = useRef<number>(Date.now())

  const resetTimers = useCallback(() => {
    // Șterge timer-urile existente
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (warningRef.current) {
      window.clearTimeout(warningRef.current)
      warningRef.current = null
    }

    // Actualizează ultima activitate
    lastActivityRef.current = Date.now()

    // Setează timer-ul de warning
    const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000
    warningRef.current = window.setTimeout(() => {
      if (onWarning) {
        onWarning()
      }
    }, warningTime)

    // Setează timer-ul de timeout
    const timeoutTime = timeoutMinutes * 60 * 1000
    timeoutRef.current = window.setTimeout(() => {
      if (onTimeout) {
        onTimeout()
      } else {
        logout()
      }
    }, timeoutTime)
  }, [timeoutMinutes, warningMinutes, onTimeout, onWarning, logout])

  const handleUserActivity = useCallback(() => {
    if (isAuthenticated) {
      resetTimers()
    }
  }, [isAuthenticated, resetTimers])

  const extendSession = useCallback(() => {
    if (isAuthenticated) {
      resetTimers()
    }
  }, [isAuthenticated, resetTimers])

  // Inițializează timer-urile când utilizatorul se autentifică
  useEffect(() => {
    if (isAuthenticated) {
      resetTimers()
    } else {
      // Șterge timer-urile când utilizatorul se deconectează
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      if (warningRef.current) {
        window.clearTimeout(warningRef.current)
        warningRef.current = null
      }
    }
  }, [isAuthenticated, resetTimers])

  // Adaugă event listener-e pentru activitatea utilizatorului
  useEffect(() => {
    if (!isAuthenticated) return

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
    
    const handleActivity = () => {
      handleUserActivity()
    }

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
    }
  }, [isAuthenticated, handleUserActivity])

  // Cleanup la unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current)
      }
      if (warningRef.current) {
        window.clearTimeout(warningRef.current)
      }
    }
  }, [])

  return {
    extendSession,
    getTimeUntilTimeout: () => {
      const timeElapsed = Date.now() - lastActivityRef.current
      const timeRemaining = (timeoutMinutes * 60 * 1000) - timeElapsed
      return Math.max(0, timeRemaining)
    },
    getTimeUntilWarning: () => {
      const timeElapsed = Date.now() - lastActivityRef.current
      const timeRemaining = ((timeoutMinutes - warningMinutes) * 60 * 1000) - timeElapsed
      return Math.max(0, timeRemaining)
    }
  }
}
